import { Login, Register, ResetPassword, MyTemplates, MockupCreator, MockupEditor, AdminFeedback, MockupGenerator, RecentGenerations, PrivacyPolicy, TermsOfUse, PublicTemplatePage, PricingPage, ListingWizard, Shops, Listings } from './services/routeViews.js'
/*
Main App component.
*/
import { isAuthenticated, isAdmin, checkAuth, getUser } from './services/auth.js'
import { Layout } from './components/common/Layout.js'
import { CookieBanner } from './components/common/CookieBanner.js'
import { Home } from './components/public/Home.js'
import { SEO } from './services/seo.js'

export default function App() {
  // Check session on load (for auto-login via cookie)
  checkAuth().catch(() => { }).finally(() => {
    // Trigger router after check
    window.dispatchEvent(new HashChangeEvent('hashchange'))
  })
  // Root container for app
  const appContainer = document.createElement('div')
  appContainer.className = 'h-screen w-full'

  // Initialize Layout once (to preserve state/sidebar if needed, though we might re-render it)
  // Actually, for cleaner switching, we might want to re-render Layout when entering app mode
  // But to keep it simple and performant, let's keep one instance and attach/detach.
  let layout = Layout('')
  let mainContent = layout.querySelector('main')
  let layoutIdentity = getUser()?.id

  // Helper to handle Auth Redirects
  const handleAuthRedirects = (hash) => {
    // Allow access to public routes
    if (!isAuthenticated() &&
      !['#home', '#workflow', '#faq', '#pricing'].includes(hash) &&
      hash !== '#generator' &&
      hash !== '#login' &&
      hash !== '#register' &&
      hash !== '#privacy-policy' &&
      hash !== '#terms-of-use' &&
      !hash.startsWith('#templates/') &&
      !hash.startsWith('#password-reset')) {

      // Default unauth behavior: go to login
      window.location.hash = '#login'
      return true
    }
    return false
  }

  // Router Function
  let routeVersion = 0
  const router = async () => {
    const version = ++routeVersion
    const attach = async promise => {
      const node = await promise
      if (version === routeVersion) appContainer.appendChild(node)
      else node.dispose?.()
    }
    try {
    let fullHash = window.location.hash || (isAuthenticated() ? '#listings' : '#home')
    // Split hash into route and search params (e.g. #login?confirmed=true)
    const [route, search] = fullHash.split('?')
    const hash = route // Use just the route part for matching logic

    // 2. Auth Guard
    if (handleAuthRedirects(hash)) return

    // Rebuild navigation when a cookie session restores a different user.
    if (layoutIdentity !== getUser()?.id) {
      mainContent.firstElementChild?.dispose?.()
      layout.dispose?.()
      layout = Layout('')
      mainContent = layout.querySelector('main')
      layoutIdentity = getUser()?.id
    }

    // 3. Route Handling
    mainContent.firstElementChild?.dispose?.()
    layout.querySelector('dialog[open]')?.close()
    appContainer.innerHTML = '<p class="p-6 text-slate-500" data-route-loading role="status">Loading…</p>'

    SEO.reset()
    SEO.setIndexable(['#home', '#workflow', '#faq'].includes(hash) || hash.startsWith('#templates/'))
    if (['#home', '#workflow', '#faq'].includes(hash)) {
      appContainer.appendChild(Home())
      if (hash !== '#home') setTimeout(() => appContainer.querySelector(hash)?.scrollIntoView(), 0)
    } else if (hash === '#login') {
      SEO.update('Sign in', 'Sign in to your Vizu Etsy listing workspace.')
      if (isAuthenticated()) {
        window.location.hash = '#listings'
        return
      }
      await attach(Login())
    } else if (hash === '#register') {
      if (isAuthenticated()) {
        window.location.hash = '#listings'
        return
      }
      await attach(Register())
    } else if (hash.startsWith('#password-reset/')) {
      // Format: #password-reset/uid/token
      const parts = hash.split('/')
      if (parts.length >= 3) {
        const uid = parts[1]
        const token = parts[2]
        await attach(ResetPassword(uid, token))
      } else {
        window.location.hash = '#login'
        return
      }
    } else {
      // APP ROUTES (Render inside Layout)
      let pageContent = null

      // Clear layout main content
      mainContent.innerHTML = ''

      if (hash === '#listings') {
        pageContent = await Listings()
      } else if (/^#listings\/[0-9a-f-]{36}$/.test(hash)) {
        pageContent = await ListingWizard(hash.split('/')[1])
      } else if (hash === '#shops') {
        pageContent = await Shops()
      } else if (hash === '#generator') {
        pageContent = await MockupGenerator()
      } else if (hash === '#recent-generations') {
        pageContent = await RecentGenerations()
      } else if (hash.startsWith('#mockup-templates/create')) {
        pageContent = await MockupCreator()
      } else if (hash.startsWith('#mockup-templates/edit/')) {
        const mockupId = hash.split('/')[2]
        if (mockupId) {
          const searchParams = new URLSearchParams(search)
          pageContent = await MockupEditor({ mockupId, searchParams })
        } else {
          window.location.hash = '#mockup-templates'
          return
        }
      } else if (hash.startsWith('#mockup-templates')) {
        pageContent = await MyTemplates()
      } else if (hash === '#admin') {
        if (!isAdmin()) {
          window.location.hash = '#generator'
          return
        }
        pageContent = await AdminFeedback()
      } else if (hash === '#privacy-policy') {
        pageContent = await PrivacyPolicy()
      } else if (hash === '#terms-of-use') {
        pageContent = await TermsOfUse()
      } else if (hash === '#pricing') {
        pageContent = await PricingPage()
      } else if (hash.startsWith('#templates/')) {
        const slug = hash.split('/')[1]
        if (slug) {
          // Public page handles its own layout (fullscreen but with back nav)
          // We can render it directly in appContainer OR inside Layout.
          // Based on design, it looks like a standalone landing page style.
          // Let's render it directly to avoid Sidebar distraction for guests.
          // But wait, the Layout provides nice structure. Let's see...
          // The component has a "Back to Templates" link.
          // Let's render it standalone for maximum SEO focus layout.
          appContainer.innerHTML = ''
          await attach(PublicTemplatePage(slug))

          if (version !== routeVersion) return
          appContainer.querySelector('[data-route-loading]')?.remove()
          const banner = CookieBanner()
          if (banner) appContainer.appendChild(banner)
          return

        } else {
          window.location.hash = '#generator'
          return
        }
      } else {
        window.location.hash = '#generator'
        return
      }

      if (version !== routeVersion) { pageContent?.dispose?.(); return }
      // Reset SEO for SPA navigation
      if (hash.startsWith('#listings')) SEO.update('Your listings', 'Prepare digital products, mockups and Etsy listing drafts in Vizu.')
      else if (hash === '#shops') SEO.update('Connections', 'Manage your Etsy and Google Drive connections in Vizu.')
      else if (hash === '#generator') SEO.update('Mockup generator for Etsy sellers', 'Create product mockups from your artwork. Try the generator, then prepare your digital listing in Vizu.')
      else if (hash === '#pricing') SEO.update('Plans and pricing', 'Explore Vizu plans for creating mockups and preparing Etsy listings.')

      // Append content to Layout
      if (pageContent instanceof Node) {
        mainContent.appendChild(pageContent)
      } else if (typeof pageContent === 'string') {
        mainContent.innerHTML = pageContent
      }

      // Append Layout to Root
      appContainer.appendChild(layout)
    }

    if (version !== routeVersion) return
    appContainer.querySelector('[data-route-loading]')?.remove()
    // Cookie Banner
    const banner = CookieBanner()
    if (banner) appContainer.appendChild(banner)
    } catch {
      if (version === routeVersion) appContainer.innerHTML = '<section class="p-8"><h1>This page could not be loaded</h1><p>Please refresh and try again.</p><a href="/">Return to Vizu</a></section>'
    }
  }

  // Listen for hash changes
  window.addEventListener('hashchange', router)

  // Initial Route
  router()

  return appContainer
}
